import React, { useMemo } from "react";
import { calculateGlobalContractStats, getAllPaymentsFromContracts, getGlobalStatsSummary } from "../../../helpers/globalContractStats";

const GlobalContractStats = ({ contracts, payments }) => {
	// Obtener todos los pagos
	const allPayments = useMemo(() => {
		return getAllPaymentsFromContracts(contracts, payments);
	}, [contracts, payments]);

	// Calcular stats
	const stats = useMemo(() => {
		return calculateGlobalContractStats(allPayments);
	}, [allPayments]);

	// Resumen amigable
	const summary = useMemo(() => {
		return getGlobalStatsSummary(stats);
	}, [stats]);

	const formatCurrency = (value) => {
		return new Intl.NumberFormat("es-MX", {
			style: "currency",
			currency: "MXN",
		}).format(value);
	};

	if (!allPayments || allPayments.length === 0) {
		return (
			<div className="p-4 bg-gray-100 rounded-lg text-center">
				<p className="text-gray-600">No hay datos de contratos</p>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{/* TÍTULO */}
			<div>
				<h2 className="text-2xl font-bold text-gray-800">
					📊 Estadísticas Globales de Contratos
				</h2>
				<p className="text-sm text-gray-600 mt-1">
					Total de pagos: {allPayments.length} | Total contractos: {contracts.length}
				</p>
			</div>

			{/* CARDS PRINCIPALES (4 COLUMNAS) */}
			<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
				{/* PAGADO */}
				<div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
					<p className="text-xs font-semibold text-gray-600 mb-1">✓ PAGADO</p>
					<p className="text-2xl font-bold text-green-700">{stats.pagado.count}</p>
					<p className="text-sm font-semibold text-green-600">{formatCurrency(stats.totalPagado)}</p>
					<p className="text-xs text-green-600 mt-2">{stats.percentPagado}%</p>
				</div>

				{/* DEUDA */}
				<div className={`rounded-lg p-4 border-2 ${stats.totalDeuda > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
					<p className="text-xs font-semibold text-gray-600 mb-1">⏰ DEUDA</p>
					<p className={`text-2xl font-bold ${stats.totalDeuda > 0 ? 'text-red-700' : 'text-gray-600'}`}>
						{stats.deuda.count}
					</p>
					<p className={`text-sm font-semibold ${stats.totalDeuda > 0 ? 'text-red-600' : 'text-gray-600'}`}>
						{formatCurrency(stats.totalDeuda)}
					</p>
				</div>

				{/* CORRIENTE */}
				<div className={`rounded-lg p-4 border-2 ${stats.totalCorriente > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200'}`}>
					<p className="text-xs font-semibold text-gray-600 mb-1">📅 CORRIENTE</p>
					<p className={`text-2xl font-bold ${stats.totalCorriente > 0 ? 'text-yellow-700' : 'text-gray-600'}`}>
						{stats.corriente.count}
					</p>
					<p className={`text-sm font-semibold ${stats.totalCorriente > 0 ? 'text-yellow-600' : 'text-gray-600'}`}>
						{formatCurrency(stats.totalCorriente)}
					</p>
				</div>

				{/* FUTURO */}
				<div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
					<p className="text-xs font-semibold text-gray-600 mb-1">📆 FUTURO</p>
					<p className="text-2xl font-bold text-blue-700">{stats.futuro.count}</p>
					<p className="text-sm font-semibold text-blue-600">{formatCurrency(stats.totalFuturo)}</p>
					<p className="text-xs text-blue-600 mt-2">{stats.percentFuturo}%</p>
				</div>
			</div>

			{/* INFORMACIÓN CRÍTICA */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
				{/* DEBE AHORA */}
				<div className={`rounded-lg p-4 border-2 ${stats.debeAhora > 0 ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-300'}`}>
					<p className={`text-sm font-semibold mb-1 ${stats.debeAhora > 0 ? 'text-red-800' : 'text-green-800'}`}>
						{stats.debeAhora > 0 ? '⚠️ DEBE PAGAR AHORA' : '✓ PAGADO'}
					</p>
					<p className={`text-3xl font-bold ${stats.debeAhora > 0 ? 'text-red-700' : 'text-green-700'}`}>
						{formatCurrency(stats.debeAhora)}
					</p>
					<p className="text-xs text-gray-600 mt-2">
						{stats.debeAhora > 0 
							? `Deuda (${stats.deuda.count}) + Corriente (${stats.corriente.count})`
							: `${stats.pagado.count} pagos completados`
						}
					</p>
				</div>

				{/* ESPERA GANAR */}
				<div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
					<p className="text-sm font-semibold mb-1 text-blue-800">
						💰 ESPERA GANAR (Próximos Meses)
					</p>
					<p className="text-3xl font-bold text-blue-700">
						{formatCurrency(stats.esperaGanar)}
					</p>
					<p className="text-xs text-gray-600 mt-2">
						{stats.futuro.count} pagos futuros
					</p>
				</div>
			</div>

			{/* BARRA DE PROGRESO */}
			<div className="bg-white border border-gray-200 rounded-lg p-4">
				<p className="text-sm font-semibold text-gray-800 mb-2">Distribución General</p>
				<div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden flex">
					<div 
						className="bg-green-500 h-full" 
						style={{ width: `${stats.percentPagado}%` }}
						title={`Pagados: ${stats.percentPagado}%`}
					></div>
					<div 
						className="bg-red-500 h-full" 
						style={{ width: `${stats.percentDeuda}%` }}
						title={`Deuda: ${stats.percentDeuda}%`}
					></div>
					<div 
						className="bg-yellow-500 h-full" 
						style={{ width: `${stats.percentCorriente}%` }}
						title={`Corriente: ${stats.percentCorriente}%`}
					></div>
					<div 
						className="bg-blue-500 h-full" 
						style={{ width: `${stats.percentFuturo}%` }}
						title={`Futuro: ${stats.percentFuturo}%`}
					></div>
				</div>

				{/* Leyenda */}
				<div className="flex flex-wrap gap-4 mt-3 text-xs">
					<span className="flex items-center gap-2">
						<span className="w-3 h-3 bg-green-500 rounded-full"></span>
						Pagados {stats.percentPagado}%
					</span>
					<span className="flex items-center gap-2">
						<span className="w-3 h-3 bg-red-500 rounded-full"></span>
						Deuda {stats.percentDeuda}%
					</span>
					<span className="flex items-center gap-2">
						<span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
						Corriente {stats.percentCorriente}%
					</span>
					<span className="flex items-center gap-2">
						<span className="w-3 h-3 bg-blue-500 rounded-full"></span>
						Futuro {stats.percentFuturo}%
					</span>
				</div>
			</div>

			{/* RESUMEN DE SITUACIÓN */}
			<div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
				<p className="text-sm font-bold text-gray-800 mb-2">Situación General</p>
				<div className="space-y-2 text-sm">
					<p>
						<span className="font-semibold">Total de pagos registrados:</span> {allPayments.length}
					</p>
					<p>
						<span className="font-semibold">Monto total manejado:</span> {formatCurrency(stats.totalGeneral)}
					</p>
					{stats.debeAhora > 0 && (
						<p className="text-red-700">
							<span className="font-semibold">⚠️ Deuda actual:</span> {formatCurrency(stats.debeAhora)}
						</p>
					)}
					<p className="text-blue-700">
						<span className="font-semibold">💰 Ingresos esperados:</span> {formatCurrency(stats.esperaGanar)}
					</p>
				</div>
			</div>
		</div>
	);
};

export default GlobalContractStats;